import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Confirmation } from 'core/components';
import { IConfirmation } from 'core/types';
import { QuoteOrderPricingType } from 'quotes/enums';
import { IQuotePart } from 'quotes/types';
import { Color } from 'theme/enums';

interface IProps {
  activeParts: IQuotePart[];
  onConfirm: (submitOrderPricingType?: QuoteOrderPricingType) => void;
  onCancel: () => void;
}

export const SubmitVendorOrderConfirmation = ({
  activeParts = [],
  onConfirm,
  onCancel,
}: IProps) => {
  const [confirmation, setConfirmation] = useState<IConfirmation | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    const baseConfirmation = {
      title: t('titleWarning'),
      message: t('messageConfirmSubmitOrder'),
      confirmBtnColor: Color.SUCCESS,
      onConfirm,
      onCancel,
    };

    const quotePartsWithoutPartNumber = activeParts.filter((part: IQuotePart) => !part.partArr?.id);
    const quotePartsWithoutPricing = activeParts.filter(
      (part: IQuotePart) => part.partArr?.id && !part.price
    );

    let quotePartsWithoutPartNumberConfirmation: IConfirmation | null = null;
    let quotePartsWithoutPricingConfirmation: IConfirmation | null = null;

    if (quotePartsWithoutPartNumber.length > 0) {
      quotePartsWithoutPartNumberConfirmation = {
        ...baseConfirmation,
        message: t('messageConfirmSubmitOrderLinesWithoutPartNumberAssigned', {
          linesWithoutPartNumber: quotePartsWithoutPartNumber
            .map((part: IQuotePart) => part.line)
            .join(', '),
        }),
      };
    }

    if (quotePartsWithoutPricing.length > 0) {
      quotePartsWithoutPricingConfirmation = {
        ...baseConfirmation,
        message: t('messageConfirmSubmitOrderLinesWithoutPricing', {
          linesWithoutPricing: quotePartsWithoutPricing
            .map((part: IQuotePart) => part.line)
            .join(', '),
        }),
        onConfirm: null,
        confirmationActions: [
          {
            label: t('buttonLabelConfirmOrderOmitPricing'),
            onClick: () => onConfirm(QuoteOrderPricingType.OMIT),
            type: QuoteOrderPricingType.OMIT,
          },
          {
            label: t('buttonLabelConfirmOrderIncludePricing'),
            onClick: () => onConfirm(QuoteOrderPricingType.INCLUDE),
            type: QuoteOrderPricingType.INCLUDE,
          },
        ],
      };
    }

    if (quotePartsWithoutPartNumberConfirmation) {
      setConfirmation({
        ...quotePartsWithoutPartNumberConfirmation,
        onConfirm: () => {
          if (quotePartsWithoutPricingConfirmation) {
            setConfirmation(quotePartsWithoutPricingConfirmation);

            return;
          }

          setConfirmation(baseConfirmation);
        },
      });

      return;
    }

    if (quotePartsWithoutPricingConfirmation) {
      setConfirmation(quotePartsWithoutPricingConfirmation);

      return;
    }

    setConfirmation(baseConfirmation);
  }, [activeParts, onCancel, onConfirm, t]);

  return <Confirmation isOpen={true} {...confirmation} />;
};
